import { Controller, useFormContext } from 'react-hook-form';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Lib
import {
    company_people_count,
    is_agency,
    is_agency_detail
} from '@/lib/signup/userInfos/marketingOptions';

import FormFieldMessage from '@/components/base/FormFieldMessage';
import Input from '@/components/base/Input';
//Components
import Label from '@/components/base/Label';
import { Select } from '@/components/base/Select/SelectComponent';
import RadioButton from '@/components/composite/Radio/RadioButton';
import RadioImage from '@/components/composite/Radio/RadioImage';
// Types
import { UserInfosFormType } from '@/components/pages/signup/userInfos/UserInfosForm';

const SubStepThreeForm = () => {
    const { t } = useTranslation('signup_funnel');

    const {
        register,
        control,
        watch,
        formState: { errors }
    } = useFormContext<UserInfosFormType>();

    return (
        <>
            <div className="flex w-full flex-col space-y-m xl:flex-row xl:space-x-3 xl:space-y-0">
                <div className="w-full">
                    <Label required htmlFor="company" fontType="input">
                        {t('user_infos.sub_step_3.company')}
                    </Label>
                    <Input
                        data-testid="company"
                        placeholder={t('user_infos.sub_step_3.company_placeholder')}
                        type="text"
                        id="company"
                        name="company"
                        error={errors?.company?.message}
                        {...register('company', {
                            required: t('user_infos.errors.error_company_required')
                        })}
                    />
                </div>
                <div className="w-full">
                    <Label required fontType="input">
                        {t('user_infos.sub_step_3.company_people_count.label')}
                    </Label>
                    <Controller
                        control={control}
                        name="company_people_count"
                        rules={{
                            validate: (el) =>
                                el !== null ||
                                t('user_infos.errors.error_company_people_count_required')
                        }}
                        render={({ field: { onChange, value } }) => (
                            <Select
                                className="max-w-full"
                                selected={
                                    company_people_count(t).find(
                                        (item) => item.value === value
                                    ) ?? {
                                        label: t(
                                            'user_infos.sub_step_3.company_people_count.placeholder'
                                        ),
                                        value: null
                                    }
                                }
                                panelOptions={{
                                    placement: 'top',
                                    optionsWrapperClassName: '!max-h-44'
                                }}
                                buttonVariant="lightBorder"
                                setSelected={(el) => {
                                    onChange(el.value);
                                }}
                                required={true}
                                error={errors?.company_people_count?.message}
                                options={company_people_count(t)}
                            />
                        )}
                    />
                </div>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-4">
                <Label required fontType="section" className="!w-auto lg:!mb-0">
                    {t('user_infos.sub_step_3.is_agency.label')}
                </Label>
                <fieldset className="flex flex-1 space-x-3">
                    {is_agency(t).map((opt) => (
                        <RadioImage
                            key={opt.value}
                            id={`is_agency_${opt.value}`}
                            src={opt.src}
                            label={opt.label}
                            value={opt.value}
                            checked={watch('is_agency') === opt.value}
                            withImgBackground={false}
                            className="min-w-[100px]"
                            data-testid={`is_agency_${opt.value}`}
                            {...register('is_agency', {
                                required: t('user_infos.errors.error_is_agency_required')
                            })}
                        />
                    ))}
                </fieldset>
            </div>
            {errors?.is_agency ? (
                <FormFieldMessage className="mt-2">{errors.is_agency.message}</FormFieldMessage>
            ) : null}

            {watch('is_agency') === 'no' ? (
                <div>
                    <Label required fontType="section">
                        {t('user_infos.sub_step_3.is_agency_detail.label')}
                    </Label>
                    <fieldset className="flex flex-row flex-wrap">
                        {is_agency_detail(t).map((opt) => (
                            <RadioButton
                                key={opt.value}
                                id={`is_agency_detail_${opt.value}`}
                                value={opt.value}
                                checked={watch('is_agency_detail') === opt.value}
                                className="mb-1.5 mr-1.5"
                                data-testid={`is_agency_detail_${opt.value}`}
                                {...register('is_agency_detail', {
                                    required:
                                        watch('is_agency') === 'no' ||
                                        t('user_infos.errors.error_is_agency_detail_required')
                                })}>
                                {opt.label}
                            </RadioButton>
                        ))}
                    </fieldset>
                    {errors?.is_agency_detail ? (
                        <FormFieldMessage className="mt-2">
                            {errors.is_agency_detail.message}
                        </FormFieldMessage>
                    ) : null}
                </div>
            ) : null}
        </>
    );
};

export default SubStepThreeForm;
